/**
 * Atendimentos — registro da chegada (administrativo ou médico).
 * Lista + "Novo atendimento": busca o colaborador, mostra as pendências dele,
 * escolhe tipo de exame e médico e navega pro detalhe (ficha → parecer → ASO).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  useAtendimentos,
  useColaboradores,
  useCriarAtendimento,
  useMedicos,
  usePendencias,
} from "@/hooks/useApi";
import type { StatusAtendimento, TipoExameOcupacional } from "@/types/api";
import { TIPO_EXAME_ROTULOS } from "@/types/api";
import { fmtData, SITUACAO_BADGE } from "@/pages/Pendencias";

export const STATUS_ATENDIMENTO_BADGE: Record<StatusAtendimento, { rotulo: string; classe: string }> = {
  em_andamento: { rotulo: "em andamento", classe: "badge bg-amber-100 text-amber-800" },
  concluido: { rotulo: "concluído", classe: "badge bg-emerald-100 text-emerald-800" },
  cancelado: { rotulo: "cancelado", classe: "badge bg-gray-100 text-ink-subtle line-through" },
};

const TIPOS: TipoExameOcupacional[] = [
  "admissional", "periodico", "retorno_trabalho",
  "mudanca_risco", "demissional", "monitoracao_pontual",
];

export function AtendimentosPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusAtendimento | "">("");
  const [busca, setBusca] = useState("");
  const { data: atendimentos, isLoading, error } = useAtendimentos({
    status, q: busca.trim() || undefined,
  });

  // ---- Novo atendimento ----
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [buscaColab, setBuscaColab] = useState("");
  const [colaboradorId, setColaboradorId] = useState<number | null>(null);
  const [tipo, setTipo] = useState<TipoExameOcupacional>("periodico");
  const [medicoId, setMedicoId] = useState<number | null>(null);

  const { data: colaboradores } = useColaboradores({ q: buscaColab || undefined });
  const { data: medicos } = useMedicos();
  const { data: pendenciasColab } = usePendencias(
    { colaborador_id: colaboradorId },
    colaboradorId !== null,
  );
  const criar = useCriarAtendimento();

  const colaborador = (colaboradores ?? []).find((c) => c.id === colaboradorId) ?? null;

  async function handleCriar() {
    if (colaboradorId === null || medicoId === null) return;
    try {
      const criado = await criar.mutateAsync({
        colaboradorId,
        body: { tipo, medico_id: medicoId },
      });
      navigate(`/atendimentos/${criado.id}`, {
        state: criado.alerta_dispensa ? { alerta_dispensa: criado.alerta_dispensa } : undefined,
      });
    } catch {
      /* erro exibido via criar.error */
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Atendimentos</h2>
          <p className="text-sm text-ink-muted">
            Registro da chegada do colaborador. Ficha clínica, exames e parecer são
            atos do perfil Médico, dentro do atendimento.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setMostrarNovo((v) => !v)}>
          {mostrarNovo ? "Fechar" : "Novo atendimento"}
        </button>
      </div>

      {mostrarNovo ? (
        <div className="card space-y-4 p-4">
          <h3 className="font-display text-base font-semibold">Novo atendimento</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted">
                Colaborador (busque por nome ou CPF)
              </label>
              <input
                className="input"
                placeholder="buscar colaborador…"
                value={buscaColab}
                onChange={(e) => { setBuscaColab(e.target.value); setColaboradorId(null); }}
              />
              <select
                className="input mt-2"
                value={colaboradorId ?? ""}
                onChange={(e) => setColaboradorId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">selecione o colaborador…</option>
                {(colaboradores ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.nome} — {c.cpf}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-muted">Tipo de exame</label>
                <select className="input" value={tipo}
                  onChange={(e) => setTipo(e.target.value as TipoExameOcupacional)}>
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>{TIPO_EXAME_ROTULOS[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-muted">Médico examinador</label>
                <select className="input" value={medicoId ?? ""}
                  onChange={(e) => setMedicoId(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">selecione o médico…</option>
                  {(medicos ?? []).map((m) => (
                    <option key={m.id} value={m.id}>{m.nome} — CRM {m.crm}/{m.uf_crm}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {colaborador && pendenciasColab ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <h4 className="text-sm font-semibold">
                Pendências de {colaborador.nome}
                <span className="ml-2 font-normal text-ink-muted">
                  ({pendenciasColab.length} pendente(s)/vencido(s))
                </span>
              </h4>
              {pendenciasColab.length === 0 ? (
                <p className="mt-1 text-sm text-emerald-700">Nenhum exame pendente ou vencido.</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {pendenciasColab.map((p, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className={SITUACAO_BADGE[p.situacao].classe}>
                        {SITUACAO_BADGE[p.situacao].rotulo}
                      </span>
                      <span>{p.exame}</span>
                      {p.vence_em ? (
                        <span className="text-xs text-ink-muted">venceu em {fmtData(p.vence_em)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <button
              className="btn-primary"
              disabled={colaboradorId === null || medicoId === null || criar.isPending}
              onClick={handleCriar}
            >
              {criar.isPending ? "Criando…" : "Criar atendimento"}
            </button>
            {criar.error ? (
              <p className="text-sm text-anarosa">{(criar.error as Error).message}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-80">
          <label className="mb-1 block text-xs font-medium text-ink-muted">
            Buscar colaborador
          </label>
          <input
            className="input"
            placeholder="nome ou CPF…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="w-48">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Status</label>
          <select className="input" value={status}
            onChange={(e) => setStatus(e.target.value as StatusAtendimento | "")}>
            <option value="">todos</option>
            <option value="em_andamento">em andamento</option>
            <option value="concluido">concluído</option>
            <option value="cancelado">cancelado</option>
          </select>
        </div>
        {busca.trim() ? (
          <button className="text-sm text-ink-muted hover:underline"
            onClick={() => setBusca("")}>limpar busca</button>
        ) : null}
      </div>

      {error ? <p className="text-sm text-anarosa">{(error as Error).message}</p> : null}
      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}

      {atendimentos && atendimentos.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Colaborador</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Médico</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {atendimentos.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-ink-muted">{fmtData(a.data_atendimento)}</td>
                  <td className="px-4 py-2 font-medium">{a.colaborador}</td>
                  <td className="px-4 py-2 text-ink-muted">{a.empresa}</td>
                  <td className="px-4 py-2">{TIPO_EXAME_ROTULOS[a.tipo]}</td>
                  <td className="px-4 py-2 text-ink-muted">{a.medico}</td>
                  <td className="px-4 py-2">
                    <span className={STATUS_ATENDIMENTO_BADGE[a.status].classe}>
                      {STATUS_ATENDIMENTO_BADGE[a.status].rotulo}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link className="btn-secondary px-2 py-1 text-xs" to={`/atendimentos/${a.id}`}>
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading && !error ? (
        <p className="text-sm text-ink-muted">Nenhum atendimento encontrado.</p>
      ) : null}
    </div>
  );
}
